import React, { FC, useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import {
  adminActions,
  adminAssignedPharmacies,
  adminResetValue,
  getAssignedPharmacies,
  getPharmaciesForPharmacyAdmin,
  IPharmaciesInPharmacyAdmin,
  pharmaciesForPharmacyAdmin,
  successSelector,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import strings from '../../../../localization';

import styles from './styles';

interface IAssignedAccountsPanel {
  adminId: string;
  adminName: string;
}

const AssignedAccountsPanel: FC<IAssignedAccountsPanel> = ({
  adminId,
  adminName,
}) => {
  const dispatch = useAppDispatch();

  const assignedPharmacies = useAppSelector(adminAssignedPharmacies);
  const allPharmacies = useAppSelector(pharmaciesForPharmacyAdmin);
  const loadSuccess = useAppSelector((state) =>
    successSelector([adminActions.getAssignedPharmacies], state));

  const { data } = allPharmacies ?? {};

  // The endpoint returns ids only, so names and emails are resolved here.
  const assignedAccounts = useMemo(
    () =>
      data.filter((item: IPharmaciesInPharmacyAdmin) =>
        assignedPharmacies.includes(item.userId)),
    [data, assignedPharmacies],
  );

  const isEmpty = loadSuccess && assignedPharmacies.length === 0;

  useEffect(() => {
    dispatch(getPharmaciesForPharmacyAdmin({ page: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (adminId) {
      dispatch(getAssignedPharmacies(adminId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue(['assignedPharmacies']));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.adminName}>{adminName}</Typography>

      {isEmpty && (
        <Typography sx={styles.empty}>{strings.noAssignedAccounts}</Typography>
      )}

      {assignedAccounts.map((item) => {
        const { userId, name, email } = item;

        return (
          <Box key={userId} sx={styles.item}>
            <Typography sx={styles.name}>{name}</Typography>
            <Typography sx={styles.email}>{email}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssignedAccountsPanel;
